import React, { useState, useEffect, useCallback } from 'react';
import axios from '../api/axiosConfig';
import * as XLSX from 'xlsx';
import './CUPLTable.css';

const DashboardCuplsModal = ({ isOpen, onClose, filterType, filterTitle, startDate, endDate }) => {
  const [loading, setLoading] = useState(false);
  const [cupls, setCupls] = useState([]);
  const [error, setError] = useState('');
  const [originFilter, setOriginFilter] = useState('todos'); // 'todos', 'tramites', 'otros'
  const [selectedTransito, setSelectedTransito] = useState('');
  const [selectedConcesionario, setSelectedConcesionario] = useState('');

  const fetchCupls = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let url = '/api/cupl?';
      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      if (filterType === 'pagados') {
        params.append('dateType', 'payment');
      }
      
      const response = await axios.get(url + params.toString());
      let data = response.data;
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (filterType === 'pagados') {
        data = data.filter(c => c.is_paid);
      } else if (filterType === 'pendientes') {
        data = data.filter(c => {
          if (c.is_paid || c.is_rejected) return false;
          if (!c.expiration_date) return true;
          const expDate = new Date(c.expiration_date);
          expDate.setHours(0, 0, 0, 0);
          return expDate >= today;
        });
      } else if (filterType === 'vencidos') {
        data = data.filter(c => {
          if (c.is_paid) return false;
          if (c.rejection_reason === 'Documento Vencido') return true;
          if (!c.expiration_date) return false;
          const expDate = new Date(c.expiration_date);
          expDate.setHours(0, 0, 0, 0);
          return expDate < today;
        });
      }

      setCupls(data);
    } catch (err) {
      console.error(err);
      setError('Error al obtener la lista de CUPLs.');
    } finally {
      setLoading(false);
    }
  }, [filterType, startDate, endDate]);

  useEffect(() => {
    if (isOpen && filterType) {
      setOriginFilter('todos');
      setSelectedTransito('');
      setSelectedConcesionario('');
      fetchCupls();
    }
  }, [isOpen, filterType, fetchCupls]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-CO');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  };

  const countTramites = cupls.filter(c => c.is_from_tramite || c.tramite_id).length;
  const countOtros = cupls.length - countTramites;

  const transitoOptions = Array.from(
    new Set(cupls.map(c => c.transito_name).filter(Boolean))
  ).sort();

  const concesionarioOptions = Array.from(
    new Set(cupls.map(c => c.concesionario_name || 'Independiente').filter(Boolean))
  ).sort();

  const filteredCupls = cupls.filter(c => {
    const isFromTramite = c.is_from_tramite || !!c.tramite_id;
    if (originFilter === 'tramites' && !isFromTramite) return false;
    if (originFilter === 'otros' && isFromTramite) return false;
    if (selectedTransito && c.transito_name !== selectedTransito) return false;
    const conc = c.concesionario_name || 'Independiente';
    if (selectedConcesionario && conc !== selectedConcesionario) return false;
    return true;
  });

  const handleExportExcel = () => {
    if (filteredCupls.length === 0) {
      alert('No hay datos para exportar.');
      return;
    }

    const dataToExport = filteredCupls.map(c => ({
      'Ref. CUPL': c.cupl_number,
      'Origen': (c.is_from_tramite || !!c.tramite_id) ? `Trámite #${c.tramite_id || ''}` : (c.source_email && c.source_email !== 'Desconocido' ? 'Correo' : 'Carga Directa'),
      'Placa': c.plate || 'SIN PLACA',
      'Trámite': c.tramite_name || 'N/A',
      'Valor': c.total_amount || c.tramite_value || 0,
      'Vencimiento': c.expiration_date ? formatDate(c.expiration_date) : '-',
      'Estado': c.is_paid ? 'Pagado' : (c.is_rejected ? 'Rechazado' : 'Pendiente'),
      'Tránsito': c.transito_name || 'N/A',
      'Concesionario': c.concesionario_name || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, filterTitle);
    XLSX.writeFile(workbook, `CUPLs_${filterTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1050 }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '950px', width: '95%' }}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <h3>📋 CUPLs: {filterTitle}</h3>
          <button className="close-btn" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}>&times;</button>
        </div>
        
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
          {error && <div className="error-banner" style={{ marginBottom: '15px', color: '#dc2626', backgroundColor: '#fef2f2', padding: '10px', borderRadius: '6px', border: '1px solid #fee2e2' }}>{error}</div>}

          {/* Filtros de origen y dropdowns */}
          {!loading && cupls.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '15px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    backgroundColor: originFilter === 'todos' ? '#2563eb' : '#e2e8f0',
                    color: originFilter === 'todos' ? '#ffffff' : '#475569',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => setOriginFilter('todos')}
                >
                  Todos ({cupls.length})
                </button>
                <button
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    backgroundColor: originFilter === 'tramites' ? '#16a34a' : '#e2e8f0',
                    color: originFilter === 'tramites' ? '#ffffff' : '#475569',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => setOriginFilter('tramites')}
                >
                  📋 De Mis Trámites ({countTramites})
                </button>
                <button
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    backgroundColor: originFilter === 'otros' ? '#0284c7' : '#e2e8f0',
                    color: originFilter === 'otros' ? '#ffffff' : '#475569',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => setOriginFilter('otros')}
                >
                  📥 Directos / Correo ({countOtros})
                </button>
              </div>

              {(transitoOptions.length > 0 || concesionarioOptions.length > 0) && (
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center', paddingTop: '6px', borderTop: '1px dashed #cbd5e1' }}>
                  {transitoOptions.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>🏛️ Tránsito:</label>
                      <select
                        value={selectedTransito}
                        onChange={e => setSelectedTransito(e.target.value)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          color: '#334155',
                          background: '#fff',
                          cursor: 'pointer',
                          outline: 'none'
                        }}
                      >
                        <option value="">Todos los tránsitos</option>
                        {transitoOptions.map(t => {
                          const count = cupls.filter(c => c.transito_name === t).length;
                          return (
                            <option key={t} value={t}>
                              {t} ({count})
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}

                  {concesionarioOptions.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>🏢 Concesionario:</label>
                      <select
                        value={selectedConcesionario}
                        onChange={e => setSelectedConcesionario(e.target.value)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          color: '#334155',
                          background: '#fff',
                          cursor: 'pointer',
                          outline: 'none'
                        }}
                      >
                        <option value="">Todos los concesionarios</option>
                        {concesionarioOptions.map(c => {
                          const count = cupls.filter(cu => (cu.concesionario_name || 'Independiente') === c).length;
                          return (
                            <option key={c} value={c}>
                              {c} ({count})
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}

                  {(selectedTransito || selectedConcesionario) && (
                    <button
                      onClick={() => {
                        setSelectedTransito('');
                        setSelectedConcesionario('');
                      }}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        color: '#dc2626',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      ✖️ Limpiar Filtros ({filteredCupls.length} resultados)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {loading ? (
            <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>⏳ Cargando lista de CUPLs...</p>
          ) : filteredCupls.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontWeight: '500' }}>
              📭 No se encontraron CUPLs para los criterios seleccionados.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="cupl-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Ref. CUPL</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Origen</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Placa</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Trámite</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Valor</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Vencimiento</th>
                  <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {filteredCupls.map(c => {
                  const isFromTramite = c.is_from_tramite || !!c.tramite_id;
                  return (
                    <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 10px', fontWeight: '600', color: '#1e293b' }}>{c.cupl_number}</td>
                      <td style={{ padding: '12px 10px' }}>
                        {isFromTramite ? (
                          <span style={{ fontSize: '11px', fontWeight: '700', color: '#15803d', background: '#dcfce7', padding: '3px 8px', borderRadius: '12px', border: '1px solid #bbf7d0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            📋 Trámite {c.tramite_id ? `#${c.tramite_id}` : ''}
                          </span>
                        ) : c.source_email && c.source_email !== 'Desconocido' ? (
                          <span style={{ fontSize: '11px', fontWeight: '700', color: '#0369a1', background: '#e0f2fe', padding: '3px 8px', borderRadius: '12px', border: '1px solid #bae6fd', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            📧 Correo
                          </span>
                        ) : (
                          <span style={{ fontSize: '11px', fontWeight: '700', color: '#475569', background: '#f1f5f9', padding: '3px 8px', borderRadius: '12px', border: '1px solid #cbd5e1', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            📥 Carga Directa
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <span className="plate" style={{ display: 'inline', fontSize: '12px', padding: '3px 8px', borderRadius: '4px', background: '#fef08a', color: '#854d0e', fontWeight: '700', border: '1px dashed #ca8a04' }}>
                          {c.plate || 'SIN PLACA'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 10px', color: '#334155', fontSize: '13px' }}>{c.tramite_name || 'N/A'}</td>
                      <td style={{ padding: '12px 10px', color: '#16a34a', fontSize: '13px', fontWeight: '600' }}>
                        {formatCurrency(c.total_amount || c.tramite_value || 0)}
                      </td>
                      <td style={{ padding: '12px 10px', color: '#64748b', fontSize: '12px' }}>
                        {formatDate(c.expiration_date)}
                      </td>
                      <td style={{ padding: '12px 10px', fontSize: '13px' }}>
                        {c.is_paid ? (
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#16a34a', background: '#dcfce7', padding: '3px 6px', borderRadius: '4px', border: '1px solid #bbf7d0' }}>Pagado</span>
                        ) : c.is_rejected ? (
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#dc2626', background: '#fef2f2', padding: '3px 6px', borderRadius: '4px', border: '1px solid #fecaca' }}>Rechazado</span>
                        ) : (
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#d97706', background: '#fef3c7', padding: '3px 6px', borderRadius: '4px', border: '1px solid #fde68a' }}>Pendiente</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          {filteredCupls.length > 0 && (
            <button onClick={handleExportExcel} className="btn-secondary" style={{ padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', border: '1px solid #059669', background: '#10b981', fontWeight: '600', color: 'white' }}>
              📥 Exportar Excel
            </button>
          )}
          <button onClick={onClose} className="btn-secondary" style={{ padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff', fontWeight: '600', color: '#475569' }}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardCuplsModal;
